import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { save } from '@/utils/xfs'
import { markTenancyDashboardListsStale } from '@/store/dashboardListStale'

export async function getTenancy (tenancyId, basicOnly = false) {
  try {
    let url = `/tenancies/${tenancyId}`
    if (basicOnly) {
      url = `${url}?basicOnly=${basicOnly}`
    }
    const response = await HTTP.get(url)
    const tenancy = response.data.data
    return tenancy
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.getTenancy.system'))
    throw err
  }
}

export async function getAllTenancies (query, { isSilentGet = false, diskCacheKey } = {}) {
  try {
    const response = await HTTP
      .silent(isSilentGet)
      .diskCache(diskCacheKey)
      .get('/tenancies/all', { params: query })
    const tenancies = response.data.data
    return tenancies
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.getTenancy.system'))
    throw err
  }
}

export async function exportAllTenancies (query) {
  const response = await HTTP.get('/tenancies/all/export', { params: query, responseType: 'arraybuffer' })
  save(response)
}

export async function getHistories (tenancyId) {
  try {
    const response = await HTTP.get(`/tenancies/${tenancyId}/histories`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.getTenancy.system'))
    throw err
  }
}

export async function createTenancy (request) {
  try {
    const response = await HTTP.post('/tenancies/', {...request})
    const tenancy = response.data.data
    markTenancyDashboardListsStale()
    return tenancy
  } catch (err) {
    notification.handleResponseError(err, 'createTenancy', i18n.t('error.createTenancy.system'))
    return {}
  }
}

export async function updateTenancy (tenancyId, request) {
  try {
    await HTTP.put(`/tenancies/${tenancyId}`, request)
    markTenancyDashboardListsStale()
    return true
  } catch (err) {
    notification.handleResponseError(err, 'updateTenancy', i18n.t('error.updateTenancy.system'))
    throw err
  }
}

export async function updateTenancyStatus (tenancyId, tenancyStatus) {
  try {
    const response = await HTTP.put(`/tenancies/${tenancyId}/status`, { status: tenancyStatus })
    markTenancyDashboardListsStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'updateTenancy')
    throw err
  }
}

export async function updateOpeningHours (tenancyId, request, isSilentUpdate) {
  try {
    await HTTP.put(`/tenancies/${tenancyId}/openinghours`, {data: request, isSilentUpdate})
    return true
  } catch (err) {
    notification.handleResponseError(err, 'updateTenancy', i18n.t('error.updateTenancy.system'))
    throw err
  }
}

export async function getOpeningHours (tenancyId) {
  try {
    const response = await HTTP.get(`/tenancies/${tenancyId}/openinghours`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'updateTenancy')
    throw err
  }
}

export async function updateTenancyContacts (tenancyId, ccdContacts) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/link`, ccdContacts)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'updateTenancyContact', i18n.t('error.updateTenancyContact'))
    throw err
  }
}

export async function linkLeasesToTenancy (tenancyId, leaseIds) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/leases`, leaseIds)
    markTenancyDashboardListsStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.addTenancyLease.system'))
    throw err
  }
}

export async function removeLeasesFromTenancy (tenancyId, leaseIds) {
  try {
    await HTTP.delete(`tenancies/${tenancyId}/leases`, { data: leaseIds })
    markTenancyDashboardListsStale()
    return true
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.deleteTenancyLease.system'))
    throw err
  }
}

export async function inviteTenantContactUser (tenancyId, contactInfo) {
  try {
    const response = await HTTP.post(`tenancies/${tenancyId}/invite`, contactInfo)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.inviteTenantContact.system'))
    throw err
  }
}

export async function uploadTenancySubAccounts (tenancyId, accounts) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/sub-accounts`, accounts)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.updateTenancyAccounts.system'))
    throw err
  }
}

export async function uploadTenancyMasterAccount (tenancyId, account) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/admin-account`, account)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.updateTenancyAccounts.system'))
    throw err
  }
}

export async function inviteTenancySubAccount (tenancyId, userId) {
  try {
    const response = await HTTP.post(`tenancies/${tenancyId}/sub-accounts/${userId}/invite`)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.updateTenancyAccounts.system'))
    throw err
  }
}

export async function getAllTenancyAccounts (tenancyId) {
  try {
    const response = await HTTP.get(`tenancies/allTenancyAccounts/${tenancyId}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function inviteContractor (tenancyId, contractorId) {
  try {
    const response = await HTTP.post(`tenancies/${tenancyId}/contractor/${contractorId}/invite`)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, null, i18n.t('error.updateTenancyAccounts.system'))
    throw err
  }
}

//  --------- Move In & Out ----------
export async function listMoveInOuts (tenancyId, params) {
  try {
    const response = await HTTP.get(`tenancies/${tenancyId}/move`, { params })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function createMoveInOutAndNotify (tenancyId, params) {
  try {
    const response = await HTTP.post(`tenancies/${tenancyId}/move`, params)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function updateMoveInOutAndNotify (tenancyId, params) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/move`, params)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function confirmContact (tenancyId) {
  try {
    const response = await HTTP.post(`tenancies/${tenancyId}/confirm_contact`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export const updateTenancyContractors = async (tenancyId, contractors) => {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/contractors`, contractors)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getOwnTenancies (isSilentGet = false) {
  try {
    let response
    if (isSilentGet) {
      response = await HTTP.silentGet('/tenancies/my')
    } else {
      response = await HTTP.get('/tenancies/my')
    }
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function getTenancyProjectGreenFitOuts (tenancyId, query) {
  try {
    const response = await HTTP.get(`/tenancies/${tenancyId}/green-fitouts`, { params: query })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateTenancyRemarks (tenancyId, payload) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/remarks`, payload)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function upsertTrRepAccounts (tenancyId, payload) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/rep_accounts`, payload)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getTrRepAccounts (tenancyId) {
  try {
    const response = await HTTP.get(`tenancies/${tenancyId}/rep_accounts`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function inviteTrRepAccounts (tenancyId, accountId) {
  try {
    const response = await HTTP.put(`tenancies/${tenancyId}/rep_accounts/${accountId}/invite`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}
